import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { fieldToTextField, TextFieldProps } from 'formik-mui';
import { useMediaMatherDropdown } from './hook';
import { MediaInfoDto } from '@sr/dto';

interface MediaOptionProps {
  option: MediaInfoDto;
}

const MediaOption = ({ option, ...props }: MediaOptionProps) => {
  const getLocationText = () => {
    const locationParts = [];
    if (option.country) {
      locationParts.push(option.country.name || String(option.country));
    }
    if (option.region) {
      locationParts.push(option.region.name || String(option.region));
    }
    if (option.city) {
      locationParts.push(option.city.name || String(option.city));
    }
    return locationParts.join(', ');
  };
  
  return (
    <Stack {...props} direction="column" sx={{ alignItems: 'flex-start' }}>
      <span style={{ textAlign: 'left', width: '100%' }}>
        {option.name}
      </span>
      <span style={{ textAlign: 'left', width: '100%', color: 'grey', fontSize: '0.875rem' }}>
        {getLocationText()} {getLocationText() && '/ '}{option.mediaType.name}
      </span>
    </Stack>
  );
};

interface MediaMatherPickerFieldProps extends TextFieldProps {
  mediaId: number; 
}

export const MediaMatherPickerField = (props: MediaMatherPickerFieldProps) => {
  const {
    mediaId,
    ...restProps
  } = props;

  const {
    form: { setFieldValue },
    field: { name, value },
    label
  } = restProps;

  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const { list, loading: fetching } = useMediaMatherDropdown(mediaId, filter);
  const [selectedMedia, setSelectedMedia] = useState<MediaInfoDto | null>(null);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: MediaInfoDto | null) => {
      setSelectedMedia(newValue);
      setFieldValue(name, newValue?.id);
    },
    [setFieldValue, name, setSelectedMedia]
  );

  const onInput = async (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  useEffect(() => {
    if (!list.length) return;
    setSelectedMedia(list.find(x => x.id === value) ?? null);
  }, [list, value]);

  const loading = open && fetching;

  const getOptionLabel = (option: MediaInfoDto | string): string => {
    if (typeof option === 'string') {
      return option;
    }
  
    const locationParts = [];
    if (option.country) {
      locationParts.push(option.country.name || String(option.country));
    }
    if (option.region) {
      locationParts.push(option.region.name || String(option.region));
    }
    if (option.city) {
      locationParts.push(option.city.name || String(option.city));
    }
  
    const locationText = locationParts.join(', ');
    if (locationText) {
      return `${option.name} - ${locationText} / ${option.mediaType.name}`;
    } else {
      return `${option.name} - ${option.mediaType.name}`;
    }
  };
  return (
    <Autocomplete
      value={selectedMedia}
      autoComplete={false}
      options={list}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onInputChange={(_event, value) => setFilter(value)}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={getOptionLabel}
      loading={loading}
      filterOptions={(options, state) => options}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      renderOption={(props, option) => <MediaOption {...props} option={option} />}
      renderInput={
        (params) => <TextField
          {...fieldToTextField(restProps)}
          {...params}
          name={name}
          label={label}
          size="small"
          placeholder="Начните набирать..."
        />
      }
    />
  );
};
